/**
 * Comprehensive profanity filter for usernames
 * Includes German, English insults, slurs, and TOS violations
 * Patterns account for common letter substitutions and misspellings
 */

// Letter substitution patterns for bypassing filters
const LEET_SPEAK: Record<string, string> = {
  a: "[a@4àáâãäåæ]",
  b: "[b8ß]",
  c: "[c(ç<{]",
  d: "[d]",
  e: "[e3€èéêë]",
  f: "[fƒ]",
  g: "[g69]",
  h: "[h#]",
  i: "[i1!|ìíîï]",
  j: "[j]",
  k: "[ck]",
  l: "[l1|]",
  m: "[m]",
  n: "[nñ]",
  o: "[o0òóôõöø]",
  p: "[p]",
  q: "[q9]",
  r: "[r]",
  s: "[s$5§]",
  t: "[t7+]",
  u: "[uùúûüv]",
  v: "[vu]",
  w: "[wvv]",
  x: "[x×]",
  y: "[yý]",
  z: "[z2]",
};

// Convert a word to a regex pattern that matches leet speak variations
function toLeetPattern(word: string): string {
  return word
    .toLowerCase()
    .split("")
    .map((char) => LEET_SPEAK[char] || char)
    .join("[\\s._-]*"); // Allow separators between letters
}

// German profanity and insults
const GERMAN_PROFANITY = [
  // Sexual/vulgar terms
  "fick",
  "ficken",
  "ficker",
  "gefickt",
  "fickend",
  "hurensohn",
  "hure",
  "nutte",
  "schlampe",
  "fotze",
  "wichser",
  "wichsen",
  "wixxer",
  "wixer",
  "schwanz",
  "penis",
  "vagina",
  "muschi",
  "möse",
  "titten",
  "arsch",
  "arschloch",
  "arschficker",
  "blasen",
  "lutschen",
  "schwuchtel",
  "lesbe",
  "pimmel",
  "hoden",
  "eier",
  "sperma",

  // General insults
  "idiot",
  "vollidiot",
  "depp",
  "volldepp",
  "trottel",
  "dummkopf",
  "dumm",
  "blöd",
  "blödmann",
  "blödian",
  "bastard",
  "mistkerl",
  "miststück",
  "dreckstück",
  "drecksau",
  "schwein",
  "sau",
  "fettsack",
  "penner",
  "assi",
  "asi",
  "assozial",
  "opfer",
  "lauch",
  "lappen",
  "loser",
  "mongo",
  "spast",
  "spasti",
  "spacko",
  "spacken",
  "behindert",
  "retard",
  "retardiert",
  "missgeburt",
  "abtreibung",
  "unfall",
  "krebs",
  "krebskind",
  "cancer",
  "kacke",
  "scheisse",
  "scheiße",
  "scheiss",
  "kotze",
  "dreck",
  "müll",

  // Racist/discriminatory (German)
  "neger",
  "nigger",
  "nigga",
  "kanake",
  "kanacke",
  "zigeuner",
  "jude",
  "nazi",
  "hitler",
  "heil",
  "sieg",
  "reich",
  "arian",
  "arier",
  "schlitzauge",
  "chink",
  "ching",
  "kümmeltürke",
  "kameltreiber",
  "ausländer",
  "flüchtling",

  // Threats/violence
  "töten",
  "umbringen",
  "sterben",
  "stirb",
  "erschießen",
  "erhängen",
  "vergasen",
  "mord",
  "mörder",
  "killer",
  "gewalt",
  "schlagen",
  "prügeln",
  "vergewaltigen",
  "vergewaltigung",
];

// English profanity and insults
const ENGLISH_PROFANITY = [
  // Sexual/vulgar terms
  "fuck",
  "fucker",
  "fucking",
  "fucked",
  "fck",
  "fuk",
  "shit",
  "shitty",
  "bullshit",
  "horseshit",
  "bitch",
  "bitchy",
  "biatch",
  "ass",
  "asshole",
  "arse",
  "arsehole",
  "cunt",
  "pussy",
  "twat",
  "whore",
  "slut",
  "hoe",
  "cock",
  "dick",
  "dickhead",
  "prick",
  "penis",
  "boob",
  "boobs",
  "tits",
  "titty",
  "titties",
  "cum",
  "jizz",
  "semen",
  "sperm",
  "blowjob",
  "handjob",
  "wank",
  "wanker",
  "jerkoff",
  "fag",
  "faggot",
  "dyke",
  "homo",
  "queer",
  "porn",
  "porno",
  "xxx",
  "nude",
  "naked",
  "sex",
  "sexy",
  "rape",
  "rapist",
  "molest",
  "masturbate",
  "masturbation",
  "dildo",
  "vibrator",
  "buttplug",

  // General insults
  "idiot",
  "moron",
  "imbecile",
  "dumb",
  "stupid",
  "retard",
  "retarded",
  "tard",
  "loser",
  "noob",
  "newb",
  "n00b",
  "lame",
  "pathetic",
  "worthless",
  "bastard",
  "scum",
  "scumbag",
  "trash",
  "garbage",
  "filth",
  "creep",
  "perv",
  "pervert",
  "pedo",
  "pedophile",
  "ugly",
  "fat",
  "fatass",
  "fatso",
  "obese",
  "cripple",
  "handicapped",
  "disabled",
  "autist",
  "autistic",
  "sperg",
  "asperger",
  "psycho",
  "schizo",
  "mental",
  "insane",
  "crazy",

  // Racist slurs (English)
  "nigger",
  "nigga",
  "negro",
  "coon",
  "darkie",
  "spook",
  "chink",
  "gook",
  "slope",
  "zipperhead",
  "spic",
  "wetback",
  "beaner",
  "greaser",
  "kike",
  "yid",
  "heeb",
  "cracker",
  "honky",
  "redneck",
  "hillbilly",
  "towelhead",
  "raghead",
  "sandnigger",
  "camel",
  "paki",
  "curry",
  "pajeet",
  "nazi",
  "hitler",
  "heil",
  "reich",
  "aryan",
  "kkk",
  "klan",
  "supremacist",
  "white power",

  // Violence/threats
  "kill",
  "murder",
  "die",
  "death",
  "shoot",
  "stab",
  "hang",
  "strangle",
  "suicide",
  "kys",
  "kms",
  "terrorist",
  "terrorism",
  "bomb",
  "massacre",
  "genocide",
  "holocaust",

  // Drugs
  "cocaine",
  "coke",
  "crack",
  "heroin",
  "meth",
  "weed",
  "marijuana",
  "cannabis",
  "pot",
  "lsd",
  "acid",
  "ecstasy",
  "mdma",
  "drug",
  "druggie",
  "junkie",
  "addict",
];

// TOS violation patterns
const TOS_VIOLATIONS = [
  // Impersonation
  "admin",
  "administrator",
  "moderator",
  "mod",
  "staff",
  "support",
  "official",
  "owner",
  "twitch",
  "youtube",
  "system",
  "server",
  "bot",

  // Spam patterns
  "free",
  "giveaway",
  "click",
  "link",
  "subscribe",
  "follow",
  "viewer",
  "viewers",
  "money",
  "bitcoin",
  "crypto",
  "nft",

  // Harmful content
  "scam",
  "hack",
  "hacker",
  "cheat",
  "cheater",
  "exploit",
  "glitch",
  "bug abuse",
  "ddos",
  "dos",
  "attack",
  "swat",
  "swatting",
  "doxx",
  "doxing",
  "leak",
];

// Additional problematic patterns
const ADDITIONAL_PATTERNS = [
  // Offensive combinations
  "gas chamber",
  "concentration camp",
  "final solution",
  "master race",
  "ethnic cleansing",
  "white genocide",
  "great replacement",

  // Sexual predator terms
  "groomer",
  "grooming",
  "minor",
  "underage",
  "child",
  "kid",
  "young",
  "little girl",
  "little boy",

  // Self-harm
  "cut myself",
  "kill myself",
  "end it all",
  "suicidal",
  "self harm",
  "self-harm",

  // Body shaming
  "anorexic",
  "bulimic",
  "eating disorder",

  // Trolling names
  "troll",
  "trolling",
  "triggered",
  "snowflake",
  "simp",
  "incel",
  "virgin",
  "karen",
  "boomer",
  "zoomer",
];

// Build the comprehensive regex patterns
function buildPatterns(): RegExp[] {
  const allWords = [
    ...GERMAN_PROFANITY,
    ...ENGLISH_PROFANITY,
    ...TOS_VIOLATIONS,
    ...ADDITIONAL_PATTERNS,
  ];

  return allWords.map((word) => {
    // For multi-word phrases, handle differently
    if (word.includes(" ")) {
      const parts = word.split(" ").map(toLeetPattern);
      return new RegExp(parts.join("[\\s._-]+"), "i");
    }
    return new RegExp(toLeetPattern(word), "i");
  });
}

// Pre-compiled patterns for performance
const PROFANITY_PATTERNS = buildPatterns();

// Additional exact match blocked names
const BLOCKED_NAMES = new Set([
  "admin",
  "administrator",
  "mod",
  "moderator",
  "system",
  "server",
  "bot",
  "official",
  "host",
  "owner",
  "creator",
  "anonymous",
  "guest",
  "user",
  "player",
  "null",
  "undefined",
  "nan",
  "void",
  "test",
  "testing",
  "debug",
]);

// Pattern for invalid characters (only allow letters, numbers, spaces, dots, underscores, hyphens)
const INVALID_CHARS_PATTERN =
  /[^a-zA-Z0-9äöüÄÖÜßàáâãåæçèéêëìíîïñòóôõøùúûýÿ\s._-]/i;

// Pattern for excessive repeating characters (e.g., "aaaaaaa")
const EXCESSIVE_REPEAT_PATTERN = /(.)\1{4,}/;

// Pattern for all numbers (no meaning)
const ALL_NUMBERS_PATTERN = /^\d+$/;

// Pattern for very short names
const MIN_NAME_LENGTH = 2;
const MAX_NAME_LENGTH = 20;

/**
 * Check if a name contains profanity or is otherwise inappropriate
 * @param name The username to check
 * @returns Object with isValid boolean and optional reason
 */
export function validateName(name: string): {
  isValid: boolean;
  reason?: string;
} {
  const trimmed = name.trim();
  const normalized = trimmed.toLowerCase();

  // Check length
  if (trimmed.length < MIN_NAME_LENGTH) {
    return { isValid: false, reason: "Name ist zu kurz (min. 2 Zeichen)" };
  }

  if (trimmed.length > MAX_NAME_LENGTH) {
    return { isValid: false, reason: "Name ist zu lang (max. 20 Zeichen)" };
  }

  // Check for invalid characters
  if (INVALID_CHARS_PATTERN.test(trimmed)) {
    return { isValid: false, reason: "Name enthält ungültige Zeichen" };
  }

  // Check for excessive repeating characters
  if (EXCESSIVE_REPEAT_PATTERN.test(normalized)) {
    return { isValid: false, reason: "Zu viele wiederholte Zeichen" };
  }

  // Check for all numbers
  if (ALL_NUMBERS_PATTERN.test(trimmed)) {
    return {
      isValid: false,
      reason: "Name darf nicht nur aus Zahlen bestehen",
    };
  }

  // Check blocked names (exact match)
  if (BLOCKED_NAMES.has(normalized)) {
    return { isValid: false, reason: "Dieser Name ist reserviert" };
  }

  // Check profanity patterns
  for (const pattern of PROFANITY_PATTERNS) {
    if (pattern.test(normalized)) {
      return { isValid: false, reason: "Name enthält unangemessene Inhalte" };
    }
  }

  // Also check against the name without spaces/separators
  const condensed = normalized.replace(/[\s._-]/g, "");
  for (const pattern of PROFANITY_PATTERNS) {
    if (pattern.test(condensed)) {
      return { isValid: false, reason: "Name enthält unangemessene Inhalte" };
    }
  }

  return { isValid: true };
}

/**
 * Quick check if name is valid (returns boolean only)
 */
export function isNameValid(name: string): boolean {
  return validateName(name).isValid;
}

/**
 * Sanitize a name by removing potentially problematic characters
 * This is a light sanitization, not a full filter
 */
export function sanitizeName(name: string): string {
  return name
    .trim()
    .replace(/[<>{}[\]\\]/g, "") // Remove potentially dangerous chars
    .slice(0, MAX_NAME_LENGTH);
}
